import { HubDto, KnownHubDtoStatus, KnownHubDtoType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { makeDefineActions } from '../../../../ui/components/pure/actions-button/actions-button.helpers';
import { ActionsButtonItem } from '../../../../ui/components/pure/actions-button/actions-button.types';
import { ActionDelete } from '../../../../ui/components/pure/icons/ActionDelete.icon';
import { ActionEnable } from '../../../../ui/components/pure/icons/ActionEnable.icon';
import { ActionDisable } from '../../../../ui/components/pure/icons/ActionDisable.icon';
import { ActionOpen } from '../../../../ui/components/pure/icons/ActionOpen.icon';
import { Permissions } from '../../../../core/permissions/permissions.typings';
import { Reload } from '../../../../ui/components/pure/icons/Reload.icon';
import { Remote } from '../../../../ui/components/pure/icons/Remote.icon';
import { Edit } from '../../../../ui/components/pure/icons/Edit.icon';

export enum HubDetailsActions {
  OPEN_ALL = 'OPEN_ALL',
  ENABLE = 'ENABLE',
  DISABLE = 'DISABLE',
  DELETE = 'DELETE',
  SYNC_KIOSK_DATA = 'SYNC_KIOSK_DATA',
  SYNC_KIOSK_DATA_SUCCESS = 'SYNC_KIOSK_DATA_SUCCESS',
  REMOTE_VIEWER = 'REMOTE_VIEWER',
  EDIT_SETTINGS = 'EDIT_SETTINGS'
}

function makeHubDetailsActions(hub: HubDto, projectID: string): ActionsButtonItem<HubDetailsActions>[] {
  if (hub.status === KnownHubDtoStatus.Deleted) {
    return [];
  }

  return [
    {
      title: 'Open all',
      Icon: ActionOpen,
      action: HubDetailsActions.OPEN_ALL,
      permission: {
        action: Permissions.hubs.openAllDoors,
        projectID
      }
    },
    {
      title: 'Enable',
      Icon: ActionEnable,
      action: HubDetailsActions.ENABLE,
      availableUiFeatures: ['internal-groundfloor'],
      permission: {
        action: Permissions.hubs.update,
        projectID
      }
    },
    {
      title: 'Disable',
      Icon: ActionDisable,
      action: HubDetailsActions.DISABLE,
      availableUiFeatures: ['internal-groundfloor'],
      permission: {
        action: Permissions.hubs.update,
        projectID
      }
    },
    {
      title: 'Delete',
      Icon: ActionDelete,
      action: HubDetailsActions.DELETE,
      availableUiFeatures: ['internal-groundfloor'],
      permission: {
        action: Permissions.hubs.delete,
        projectID
      }
    },
    {
      title: 'Edit settings',
      Icon: Edit,
      action: HubDetailsActions.EDIT_SETTINGS,
      availableUiFeatures: ['internal-groundfloor'],
      // Gate on the managing project (where the settings live), which may differ from the current one.
      permission: {
        action: Permissions.hubs.update,
        projectID: hub.managedByProjectID
      }
    },
    {
      title: 'Sync Kiosk Data',
      Icon: Reload,
      action: HubDetailsActions.SYNC_KIOSK_DATA,
      availableUiFeatures: ['internal-groundfloor'],
      permission: {
        action: Permissions.hubs.update,
        projectID
      }
    },
    {
      title: 'Remote viewer',
      Icon: Remote,
      action: HubDetailsActions.REMOTE_VIEWER,
      availableUiFeatures: ['internal-groundfloor']
    }
  ];
}

function makeValidateAction(hub: HubDto) {
  return function validateAction(action: HubDetailsActions) {
    const isExternalLockersHub = hub.type === KnownHubDtoType.LockersHubExternal;

    switch (action) {
      case HubDetailsActions.OPEN_ALL:
        return !isExternalLockersHub && hub.status !== KnownHubDtoStatus.Deleted;
      case HubDetailsActions.ENABLE:
        return !isExternalLockersHub && hub.status !== KnownHubDtoStatus.Active;
      case HubDetailsActions.DISABLE:
        return !isExternalLockersHub && hub.status === KnownHubDtoStatus.Active;
      case HubDetailsActions.DELETE:
        return hub.status !== KnownHubDtoStatus.Deleted;
      case HubDetailsActions.SYNC_KIOSK_DATA:
        return !isExternalLockersHub && hub.status === KnownHubDtoStatus.Active;
      case HubDetailsActions.REMOTE_VIEWER:
        return !isExternalLockersHub && hub.status !== KnownHubDtoStatus.Deleted;
      case HubDetailsActions.EDIT_SETTINGS:
        return hub.status !== KnownHubDtoStatus.Deleted;
      default:
        return false;
    }
  };
}

export function makeDefineHubDetailsActions(hub: HubDto, projectID: string) {
  return makeDefineActions(makeHubDetailsActions(hub, projectID), undefined, makeValidateAction(hub));
}
